import "server-only"

import type {
  CreateProductReviewResponse,
  ProductReviewPermissionResponse,
  ProductReviewsResponse,
} from "@/api/product-reviews"
import type {
  ReviewMedia,
  ReviewsSummaryData,
} from "@/components/shared/reviews/types"
import { env } from "@/env"
import type { AuthSessionUser } from "@/services/auth-service"
import type { MediaAttachment } from "@/services/media-upload-service"
import {
  getFreshProductReviews,
  getProductReviews,
  getUserPendingReviews,
  type WooCommerceReview,
} from "@/services/woo-commerce-service"

const WP_JSON_REGEX = /\/wp-json\/?$/

import {
  getWordPressAccountOrders,
  type WordPressMyOrderLineItem,
} from "@/services/wordpress-service"

const DEFAULT_REVIEW_AVATAR = "/static/img/user/default-user.jpg"
const INITIAL_REVIEW_ORDER_FETCH_LIMIT = 100
const QUALIFYING_REVIEW_ORDER_STATUSES = new Set(["processing", "completed"])

// Extract attachment IDs from review comment meta
// Customer Reviews plugin stores attachment data in comment meta
// Since WordPress REST API requires auth for comment meta, we scrape the product page HTML
const extractReviewMedia = async (
  review: WooCommerceReview
): Promise<ReviewMedia[]> => {
  const media: ReviewMedia[] = []

  try {
    const axios = await import("axios").then((m) => m.default)
    const { load } = await import("cheerio")

    // Fetch the product page HTML where reviews are rendered with images
    const productUrl =
      review.product_permalink ||
      `${env.WP_API_URL.replace(WP_JSON_REGEX, "")}/produto/?p=${review.product_id}`

    const pageResponse = await axios.get(productUrl)
    const $ = load(pageResponse.data)

    // Find the review comment div by ID
    const reviewElement = $(`#comment-${review.id}`)

    if (reviewElement.length > 0) {
      // Find images within the review
      const imageElements = reviewElement.find(
        ".cr-comment-image img, .iv-comment-image img"
      )

      imageElements.each((_, img) => {
        const src = $(img).attr("src")
        if (src) {
          // Extract attachment ID from data attribute or URL
          const attachmentId = Number.parseInt(
            $(img).closest("[data-attachment-id]").attr("data-attachment-id") ||
              "0",
            10
          )

          media.push({
            id: attachmentId || Date.now(), // Use timestamp as fallback ID
            url: src.replace("?crsrc=wp", ""), // Remove query param
            type: "image",
          })
        }
      })

      // Find videos within the review
      const videoElements = reviewElement.find(
        ".cr-comment-video video, .iv-comment-video video"
      )

      videoElements.each((_, video) => {
        const src = $(video).attr("src") || $(video).find("source").attr("src")
        if (src) {
          const attachmentId = Number.parseInt(
            $(video)
              .closest("[data-attachment-id]")
              .attr("data-attachment-id") || "0",
            10
          )

          media.push({
            id: attachmentId || Date.now(),
            url: src,
            type: "video",
          })
        }
      })
    }
  } catch {
    // Silently fail - review will be displayed without media
  }
  return media
}

const normalizeReviewRating = (rating: number): number =>
  Math.min(5, Math.max(0, Math.round(Number(rating) || 0)))

const normalizeReview = async (
  review: WooCommerceReview
): Promise<ProductReviewsResponse["reviews"][number]> => {
  const content = review.review?.trim()
  const media = await extractReviewMedia(review)

  return {
    id: review.id,
    author: review.reviewer || "Cliente",
    rating: normalizeReviewRating(review.rating),
    content: content ? review.review : null,
    date: review.date_created,
    verified: Boolean(review.verified),
    avatar: review.reviewer_avatar_urls?.[48] || DEFAULT_REVIEW_AVATAR,
    isPending: review.status === "hold",
    media: media.length > 0 ? media : undefined,
  }
}

export const buildProductReviewsSummary = (
  reviews: WooCommerceReview[]
): ReviewsSummaryData => {
  const total = reviews.length
  const totalRating = reviews.reduce(
    (sum, review) => sum + normalizeReviewRating(review.rating),
    0
  )

  const distribution = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter(
      (review) => normalizeReviewRating(review.rating) === star
    ).length

    return {
      star,
      count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
    }
  })

  return {
    total,
    average: total > 0 ? Number((totalRating / total).toFixed(1)) : 0,
    distribution,
  }
}

export const getProductReviewsFeed = async (
  productId: number,
  options: {
    fresh?: boolean
    currentUserEmail?: string
  } = {}
): Promise<ProductReviewsResponse> => {
  const reviews = options.fresh
    ? await getFreshProductReviews(productId)
    : await getProductReviews(productId)

  // If user is logged in, fetch and merge their pending reviews
  let allReviews = reviews
  if (options.currentUserEmail) {
    const pendingReviews = await getUserPendingReviews(
      productId,
      options.currentUserEmail
    )
    // Add pending reviews at the top, avoiding duplicates
    const existingIds = new Set(reviews.map((r) => r.id))
    const newPendingReviews = pendingReviews.filter(
      (r) => !existingIds.has(r.id)
    )
    allReviews = [...newPendingReviews, ...reviews]
  }

  return {
    ok: true,
    reviews: await Promise.all(allReviews.map(normalizeReview)),
    summary: buildProductReviewsSummary(reviews), // Summary excludes pending
  }
}

const getOrderItems = (
  items:
    | WordPressMyOrderLineItem[]
    | Record<string, WordPressMyOrderLineItem>
    | undefined
): WordPressMyOrderLineItem[] => {
  if (!items) {
    return []
  }

  return Array.isArray(items) ? items : Object.values(items)
}

const hasPurchasedProduct = async (
  sessionCookie: string,
  productId: number
): Promise<boolean> => {
  let limit = INITIAL_REVIEW_ORDER_FETCH_LIMIT
  let previousOrderCount = -1

  while (true) {
    // This endpoint currently exposes a growing limit, not page-based access.
    const orders = await getWordPressAccountOrders(sessionCookie, limit)

    const hasMatchingOrder = orders.some((order) => {
      if (!QUALIFYING_REVIEW_ORDER_STATUSES.has(order.status)) {
        return false
      }

      return getOrderItems(order.items).some(
        (item) => Number(item.product_id) === productId
      )
    })

    if (hasMatchingOrder) {
      return true
    }

    if (orders.length < limit || orders.length === previousOrderCount) {
      return false
    }

    previousOrderCount = orders.length
    limit *= 2
  }
}

const hasExistingReview = async (
  productId: number,
  currentUser: AuthSessionUser
): Promise<boolean> => {
  const reviews = await getFreshProductReviews(productId)
  const normalizedEmail = currentUser.email.trim().toLowerCase()
  const normalizedName = currentUser.name.trim().toLowerCase()

  return reviews.some((review) => {
    const reviewer = review.reviewer.trim().toLowerCase()

    return reviewer === normalizedName || reviewer === normalizedEmail
  })
}

export const getProductReviewPermission = async ({
  productId,
  sessionCookie,
  currentUser,
}: {
  productId: number
  sessionCookie: string
  currentUser: AuthSessionUser
}): Promise<ProductReviewPermissionResponse> => {
  const purchased = await hasPurchasedProduct(sessionCookie, productId)

  if (!purchased) {
    return {
      ok: true,
      canReview: false,
      reason: "not_purchased",
      user: currentUser,
    }
  }

  const alreadyReviewed = await hasExistingReview(productId, currentUser)

  if (alreadyReviewed) {
    return {
      ok: true,
      canReview: false,
      reason: "already_reviewed",
      user: currentUser,
    }
  }

  return {
    ok: true,
    canReview: true,
    reason: "purchased",
    user: currentUser,
  }
}

export const submitProductReview = async ({
  productId,
  sessionCookie,
  currentUser,
  rating,
  content,
  attachments = [],
}: {
  productId: number
  sessionCookie: string
  currentUser: AuthSessionUser
  rating: 1 | 2 | 3 | 4 | 5
  content: string
  attachments?: MediaAttachment[]
}): Promise<CreateProductReviewResponse> => {
  const permission = await getProductReviewPermission({
    productId,
    sessionCookie,
    currentUser,
  })

  if (!permission.canReview) {
    return {
      ok: false,
      code: "REVIEW_NOT_ALLOWED",
      message: "Somente clientes que compraram este produto podem avaliar.",
    }
  }

  const axios = await import("axios").then((m) => m.default)
  const wpBaseUrl = env.WP_API_URL.replace(WP_JSON_REGEX, "")

  // Try to submit via Customer Reviews plugin's native WordPress form endpoint
  if (attachments.length > 0) {
    try {
      const formData = new FormData()
      formData.append("comment_post_ID", String(productId))
      formData.append("rating", String(rating))
      formData.append("comment", content)
      formData.append("author", currentUser.name)
      formData.append("email", currentUser.email)
      formData.append("comment_parent", "0")

      // Add the uploaded image references as JSON strings (per plugin's requirement)
      // The plugin expects an array of JSON strings: ['{"id":123,"key":"abc"}', '{"id":456,"key":"def"}']
      attachments.forEach((attachment) => {
        formData.append(
          "cr-upload-images-ids[]",
          JSON.stringify({ id: attachment.id, key: attachment.key })
        )
      })

      const response = await axios.post(
        `${wpBaseUrl}/wp-comments-post.php`,
        formData,
        {
          headers: {
            Cookie: sessionCookie,
            "Content-Type": "multipart/form-data",
          },
          maxRedirects: 0,
          validateStatus: (status) => status < 400 || status === 302,
        }
      )

      // WordPress returns 302 redirect on success
      if (response.status === 302) {
        // Wait a moment for WordPress to process the comment
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Fetch fresh reviews to get the newly created one
        const freshReviews = await getFreshProductReviews(productId)
        const newReview = freshReviews.find(
          (r) =>
            r.reviewer_email?.toLowerCase() ===
              currentUser.email.toLowerCase() &&
            r.status === "hold" &&
            // Check if it was created recently (within last 2 minutes)
            new Date(r.date_created).getTime() > Date.now() - 120_000
        )

        if (newReview) {
          return {
            ok: true,
            review: await normalizeReview(newReview),
          }
        }

        // Even if we can't find it, the review was created successfully
        // Return a generic success response to avoid duplication
        return {
          ok: true,
          review: {
            id: 0, // Temporary ID
            author: currentUser.name,
            rating,
            content,
            date: new Date().toISOString(),
            verified: false,
            avatar: "/static/img/user/default-user.jpg",
            isPending: true,
            media: attachments.map((a) => ({
              id: a.id,
              url: a.url,
              type: a.type,
            })),
          },
        }
      }
    } catch {
      // Fall through to WooCommerce REST API method
    }
  }

  // Fallback: Submit review via WooCommerce REST API
  const reviewPayload: Record<string, unknown> = {
    product_id: productId,
    review: content,
    reviewer: currentUser.name,
    reviewer_email: currentUser.email,
    rating,
    status: "hold",
  }

  // Format attachments for WordPress Customer Reviews plugin
  if (attachments.length > 0) {
    // Format as array of {id, key, nonce} objects - include all fields from upload response
    const imageData = attachments.map((attachment) => ({
      id: attachment.id,
      key: attachment.key,
      nonce: attachment.nonce,
    }))

    // Add as WooCommerce meta_data with both possible meta keys the plugin might use
    reviewPayload.meta_data = [
      {
        key: "ivole_images",
        value: JSON.stringify(imageData),
      },
      {
        key: "cr_images",
        value: JSON.stringify(imageData),
      },
    ]
  }

  let createdReview: WooCommerceReview
  try {
    const wooCommerceApi = await import("./api").then((m) => m.wooCommerceApi)
    const response = await wooCommerceApi.post<WooCommerceReview>(
      "/products/reviews",
      reviewPayload
    )
    createdReview = response.data
  } catch {
    return {
      ok: false,
      code: "PRODUCT_REVIEW_SUBMIT_FAILED",
      message: "Nao foi possivel enviar a avaliacao do produto.",
    }
  }

  // If attachments were provided, link them to the review via WordPress REST API
  if (attachments.length > 0 && createdReview.id) {
    try {
      const imageData = attachments.map((a) => ({
        id: a.id,
        key: a.key,
        nonce: a.nonce,
      }))

      // Use WordPress REST API to update the comment meta
      // PUT request to update existing comment with meta fields
      await axios.put(
        `${env.WP_API_URL}/wp/v2/comments/${createdReview.id}`,
        {
          meta: {
            ivole_images: JSON.stringify(imageData),
            cr_images: JSON.stringify(imageData),
          },
        },
        {
          headers: {
            Cookie: sessionCookie,
            "Content-Type": "application/json",
          },
        }
      )
    } catch {
      // Don't fail the whole operation - the review was created successfully
    }
  }

  return {
    ok: true,
    review: await normalizeReview(createdReview),
  }
}
